/**
 * P-B 详情长图 — 区块模型(与 ai `longimage/models.py::STYLE_SPECS` 33 样式白名单对齐)。
 *
 * 三原语:text(排版渲染)/ image(唯一 AI 出图)/ data(真数据模板渲染,不 AI 编数字)。
 * 10 用途默认骨架 + 横宽 4 版式 / 8 尺寸预设逐字对齐设计稿(Artifact f4d30580)。
 */
import type { LongImageCreatePayload } from "../../api/posterService";
import type { PosterExplosiveHandoff } from "../../model/blueprint";
import { deriveGroupPoints } from "../../model/blueprint";
import type { StyleTokens } from "./canvas";

export type Primitive = "text" | "image" | "data";

export interface LongImageBlockForm {
  id: string;
  primitive: Primitive;
  styleTag: string;
  name: string;
  props: Record<string, unknown>;
}

// B2 场景卡横切:用途 key 单一源收口到 entities/scene-card(卡 preset 与搭建器同一份枚举)。
export type UseCase = import("@/entities/scene-card/model/presets").LongImageUsage;

export const USE_CASES: Array<{ key: UseCase; label: string }> = [
  { key: "goods", label: "电商详情" },
  { key: "corp", label: "企业介绍" },
  { key: "event", label: "活动海报" },
  { key: "review", label: "产品测评" },
  { key: "report", label: "数据战报" },
  { key: "hire", label: "招聘" },
  { key: "menu", label: "菜单价目" },
  { key: "tutorial", label: "教程攻略" },
  { key: "rank", label: "榜单" },
  { key: "social", label: "社交长图" },
];

/**
 * 竖长搭建器页头标题:用途 → 与卡墙同形的名字(label 不带「图」字尾的补「长图」,goods →「电商详情长图」);
 * 没定目的(直进)→ 通用「详情长图」。★1004 T3:备料页与编排页同一个函数,不再一页一个名。
 */
export function longImageTitleOf(useCase: string | null | undefined): string {
  if (!useCase) return "详情长图";
  const label = USE_CASES.find((u) => u.key === useCase)?.label;
  if (!label) return "详情长图";
  return label.endsWith("图") ? label : `${label}长图`;
}

/**
 * 搭建器页头标题**单源**(1004 第三轮):备料页与编排页同一个函数、同一组入参。
 *
 * ★复测:自由创作进来备料页叫「详情长图」,点智能编排后编排页变「电商详情长图」—— 编排页读的是搭建器
 *   内部的 useCase,而那个值在没选用途时是**默认值 goods**,不是用户选的。标题只认「进来时定下的用途」
 *   (用途卡 / 模板 / 回改那一稿带的),编排前后不变;没定用途 → 通用「详情长图」。
 */
export function longImagePageTitle(
  direction: "v" | "h",
  entryUseCase: string | null | undefined
): string {
  if (direction === "h") return "横宽宽图";
  return longImageTitleOf(entryUseCase);
}

/** styleTag → {原语, 展示名, 徽章}(= ai STYLE_SPECS 白名单;新样式两端同步登记)。 */
export const STYLE_META: Record<string, { primitive: Primitive; label: string; badge: string }> = {
  bigtext: { primitive: "text", label: "大字", badge: "文案" },
  golden: { primitive: "text", label: "金句", badge: "文案" },
  body: { primitive: "text", label: "正文", badge: "文案" },
  cite: { primitive: "text", label: "引用", badge: "文案" },
  cta: { primitive: "text", label: "CTA", badge: "文案" },
  numlist: { primitive: "text", label: "编号列表", badge: "文案" },
  twocol: { primitive: "text", label: "双栏", badge: "文案" },
  hero: { primitive: "image", label: "首屏大图", badge: "图" },
  scene: { primitive: "image", label: "卖点场景", badge: "图" },
  white: { primitive: "image", label: "白底图", badge: "图" },
  usage: { primitive: "image", label: "使用场景", badge: "图" },
  real: { primitive: "image", label: "实拍", badge: "图" },
  imagecard: { primitive: "image", label: "图文卡", badge: "图" },
  // ★实拍分格卡(2026-08-05 对齐模板样图):多张实拍并排,每张配矢量标题+说明,不叫引擎
  photocards: { primitive: "image", label: "实拍分格卡", badge: "实拍" },
  grid9: { primitive: "image", label: "九宫格", badge: "图" },
  kv: { primitive: "image", label: "主视觉KV", badge: "图" },
  spec: { primitive: "data", label: "规格参数", badge: "参数" },
  comparebar: { primitive: "data", label: "对比条", badge: "报表" },
  percent: { primitive: "data", label: "百分比", badge: "报表" },
  beforeafter: { primitive: "data", label: "前后对比", badge: "报表" },
  stat: { primitive: "data", label: "统计大牌", badge: "报表" },
  timeline: { primitive: "data", label: "时间轴", badge: "时间轴" },
  flow: { primitive: "data", label: "流程", badge: "流程" },
  infocard: { primitive: "data", label: "信息卡", badge: "信息" },
  rank: { primitive: "data", label: "榜单", badge: "榜单" },
  table: { primitive: "data", label: "表格", badge: "报表" },
  price: { primitive: "data", label: "价目表", badge: "价目" },
  line: { primitive: "data", label: "折线", badge: "折线" },
  pie: { primitive: "data", label: "饼图", badge: "饼图" },
  rating: { primitive: "data", label: "评分", badge: "评分" },
  sales: { primitive: "data", label: "销量好评", badge: "背书" },
  honor: { primitive: "data", label: "资质荣誉", badge: "荣誉" },
  media: { primitive: "data", label: "媒体", badge: "媒体" },
  qr: { primitive: "data", label: "二维码", badge: "码" },
};

/** 样式库 palette 分组(加块面板;顺序=设计稿)。 */
export const PALETTE_GROUPS: Array<{ primitive: Primitive; label: string; tags: string[] }> = [
  {
    primitive: "text",
    label: "文字",
    tags: ["bigtext", "golden", "body", "cite", "numlist", "twocol", "cta"],
  },
  {
    primitive: "image",
    label: "图片 · 唯一 AI 出图",
    tags: ["hero", "scene", "white", "usage", "real", "imagecard", "photocards", "grid9", "kv"],
  },
  {
    primitive: "data",
    label: "数据 · 真数据不编",
    tags: [
      "spec",
      "comparebar",
      "percent",
      "beforeafter",
      "stat",
      "timeline",
      "flow",
      "infocard",
      "rank",
      "table",
      "price",
      "line",
      "pie",
      "rating",
      "sales",
      "honor",
      "media",
      "qr",
    ],
  },
];

let seq = 0;
const nid = () => `b${Date.now().toString(36)}${(seq++).toString(36)}`;

/**
 * 图块画面描述的占位串 —— **唯一定义处**(D-12,2026-08-11)。
 * 提交闸的判据是「值字面等于它」,此前字面量散在建块/闸两处,漂一个字闸就失效
 * (「在此填写文案…」vs「在此填写文案」正是这么漏过的)。
 */
export const IMAGE_PROMPT_PLACEHOLDER = "在此描述画面…";

/** 每样式默认 props(占位内容,用户在 inspector 改;满足 ai 侧最小校验)。 */
/**
 * 图块专属的 props 键(ai 侧 ImageProps 的字段 + console 落库形态的 refKey/refKeys)。
 *
 * ★0917 测试机真单(5872549102593 等 7 单):「上传参考图」的回调落到了当时选中的「正文」
 *   文本块,body 带上 refKey+genMode,ai 侧 TextProps extra=forbid 把**整单**打回
 *   (`blocks.5.genMode —— Extra inputs are not permitted`),右列实时预览同源整列 502。
 * 这份判据三处共用:写回口(useLongImageBuilder/useCanvasBuilder.updateProps)、
 * 组包出口(buildLongImagePayload/buildCanvasCreatePayload)、预览请求(useLiveBandPreview)。
 * ai 侧同名集合 `_IMAGE_ONLY_PROP_KEYS`(longimage/models.py)—— 两边一起改。
 */
export const IMAGE_ONLY_PROP_KEYS: ReadonlySet<string> = new Set([
  "genMode",
  "refKey",
  "refKeys",
  "refUrl",
  "refUrls",
  "prompt",
  "slots",
  "overlayText",
  "overlayLines",
  "blockRatio",
  "refHasText",
  "refTextSafe",
]);

/** 非图块上的图块专属键剥掉;图块、或没得剥时返回**同一引用**(memo/digest 不抖)。 */
export function stripImageOnlyProps(
  primitive: Primitive,
  props: Record<string, unknown>
): Record<string, unknown> {
  if (primitive === "image") return props;
  if (!Object.keys(props).some((k) => IMAGE_ONLY_PROP_KEYS.has(k))) return props;
  return Object.fromEntries(Object.entries(props).filter(([k]) => !IMAGE_ONLY_PROP_KEYS.has(k)));
}

export function defaultPropsFor(styleTag: string): Record<string, unknown> {
  const meta = STYLE_META[styleTag];
  if (!meta) return {};
  if (meta.primitive === "image") {
    return { genMode: "t2i", prompt: IMAGE_PROMPT_PLACEHOLDER, blockRatio: "3:4" };
  }
  switch (styleTag) {
    case "numlist":
    case "twocol":
      return { items: ["第一条", "第二条"] };
    case "cite":
      return { text: "在此填写引用内容", source: "— 出处" };
    case "cta":
      return { text: "立即下单 →", bg: "accent" };
    case "spec":
    case "infocard":
    case "price":
    case "rank":
    case "timeline":
    case "flow":
    case "sales":
    case "honor":
    case "media":
      return { items: [{ label: "名称", value: "内容" }] };
    case "stat":
    case "percent":
    case "beforeafter":
      return { items: [{ value: "99%", label: "指标名" }] };
    case "comparebar":
    case "line":
    case "pie":
      return {
        series: [
          { label: "本品", value: 3, highlight: true },
          { label: "传统", value: 30 },
        ],
      };
    case "table":
      return { headers: ["列一", "列二"], rows: [["内容", "内容"]] };
    case "rating":
      return { scale: 5, items: [{ label: "维度", score: 4.8 }] };
    case "qr":
      return { content: "https://", caption: "扫码了解更多" };
    default:
      return { text: "在此填写文案…" };
  }
}

export function makeBlock(styleTag: string, name?: string): LongImageBlockForm {
  const meta = STYLE_META[styleTag];
  return {
    id: nid(),
    primitive: meta.primitive,
    styleTag,
    name: name ?? meta.label,
    props: defaultPropsFor(styleTag),
  };
}

/** 10 用途默认骨架(逐字对齐设计稿 ol-* 区块列表)。 */
export function skeletonFor(useCase: UseCase): LongImageBlockForm[] {
  const spec: Record<UseCase, Array<[string, string]>> = {
    goods: [
      ["hero", "首屏大图"],
      ["bigtext", "痛点大字"],
      ["scene", "核心卖点"],
      ["spec", "规格参数"],
      ["comparebar", "数据对比"],
      ["usage", "使用场景"],
      ["sales", "好评背书"],
      ["cta", "品牌 CTA"],
    ],
    corp: [
      ["kv", "品牌主视觉"],
      ["golden", "愿景 slogan"],
      ["stat", "数据业绩"],
      ["timeline", "发展历程"],
      ["honor", "资质荣誉"],
      ["real", "核心团队"],
      ["infocard", "联系方式"],
    ],
    event: [
      ["kv", "活动主视觉"],
      ["bigtext", "活动主题"],
      ["scene", "活动亮点"],
      ["infocard", "时间地点"],
      ["flow", "参与流程"],
      ["qr", "报名二维码"],
    ],
    review: [
      ["hero", "测评封面"],
      ["bigtext", "测评结论"],
      ["rating", "维度评分"],
      ["comparebar", "实测数据"],
      ["twocol", "优缺点"],
      ["cta", "购买 CTA"],
    ],
    report: [
      ["hero", "战报封面"],
      ["bigtext", "核心结论"],
      ["stat", "关键指标"],
      ["line", "趋势折线"],
      ["pie", "结构饼图"],
      ["rank", "销量榜单"],
    ],
    hire: [
      ["kv", "招聘主视觉"],
      ["body", "我们是谁"],
      ["infocard", "岗位列表"],
      ["infocard", "薪资福利"],
      ["flow", "成长路径"],
      ["qr", "投递二维码"],
    ],
    menu: [
      ["hero", "品牌头图"],
      ["imagecard", "招牌推荐"],
      ["price", "分类价目"],
      ["price", "套餐组合"],
      ["infocard", "门店信息"],
      ["qr", "点单二维码"],
    ],
    tutorial: [
      ["hero", "封面标题"],
      ["body", "简介"],
      ["numlist", "操作步骤"],
      ["cite", "小贴士"],
      ["real", "成品展示"],
      ["cta", "关注 CTA"],
    ],
    rank: [
      ["hero", "榜单封面"],
      ["body", "榜单说明"],
      ["rank", "TOP 榜单"],
      ["infocard", "数据来源"],
      ["cta", "关注 CTA"],
    ],
    social: [
      ["kv", "主视觉"],
      ["golden", "金句开场"],
      ["body", "正文段落"],
      ["scene", "配图"],
      ["cite", "引用"],
      ["cta", "结尾 CTA"],
    ],
  };
  return spec[useCase].map(([tag, name]) => makeBlock(tag, name));
}

/** 横宽 4 版式(分区数)+ 8 尺寸预设(逐字对齐设计稿)。 */
export const WIDE_LAYOUTS: Array<{
  key: "lr" | "tb" | "center" | "three";
  label: string;
  zones: number;
}> = [
  { key: "lr", label: "左图右文", zones: 2 },
  { key: "tb", label: "上下", zones: 2 },
  { key: "center", label: "居中叠字", zones: 1 },
  { key: "three", label: "三分", zones: 3 },
];

export const WIDE_PRESETS: Array<{ key: string; label: string; w: number; h: number }> = [
  { key: "banner", label: "店铺 banner", w: 1920, h: 640 },
  { key: "mp", label: "公众号封面", w: 900, h: 383 },
  { key: "vcover", label: "视频封面", w: 1280, h: 720 },
  { key: "live", label: "直播背景", w: 1920, h: 1080 },
  { key: "webhero", label: "官网首屏图", w: 1920, h: 800 },
  { key: "kv", label: "发布会 KV", w: 1920, h: 1080 },
  { key: "ppt", label: "PPT 单页", w: 1280, h: 720 },
  { key: "outdoor", label: "户外大屏", w: 3840, h: 1080 },
];

/** 横宽默认分区块(版式切换时重建)。 */
export function wideSkeletonFor(layout: "lr" | "tb" | "center" | "three"): LongImageBlockForm[] {
  switch (layout) {
    case "center":
      return [makeBlock("kv", "主视觉")];
    case "three":
      return [
        makeBlock("hero", "左区"),
        makeBlock("bigtext", "中区标题"),
        makeBlock("scene", "右区"),
      ];
    default:
      return [makeBlock("hero", "产品主图"), makeBlock("bigtext", "标题文案")];
  }
}

/**
 * 蓝图 → 初始区块序列(goods 骨架 + 卖点位):爆点决定**有几个卖点位、什么顺序**,
 * 痛点大字/规格参数/CTA 骨架保留。
 *
 * ★2026-08-12:爆点**不再写进 `overlayText` / `prompt`**。
 *
 * 爆点是**参考片**的记忆点(「GEO就是被AI选中」「品牌不在AI答案里=被隐身」),
 * 不是用户产品的卖点。原来它直接落进叠字(真印在成图上)和出图指令,于是一家
 * 400 电话公司的详情图上印着别人家的宣传语 —— 与追爆舱页面上写的
 * 「只学套路不搬内容」当面矛盾,还是把竞品文案印进自家物料的实打实风险。
 *
 * 保留下来的是**结构**:几位卖点、什么顺序 —— 那才是套路给的东西。
 * 参考句留在区块名里(编辑器可见、永不进图),让用户知道这一位该写什么。
 */
export function buildBlocksFromBlueprint(handoff: PosterExplosiveHandoff): LongImageBlockForm[] {
  const points = deriveGroupPoints(handoff);
  const head = [makeBlock("hero", "首屏大图"), makeBlock("bigtext", "痛点大字")];
  // 一个爆点都没有(蓝图库老数据/报告缺失)也要留一位,否则蓝图路铺出来的骨架
  // 与「什么都没带」一模一样,用户根本看不出套路带了什么。
  const slots = points.length > 0 ? points : [null];
  const pointBlocks = slots.map((p, i) => {
    // 参考句在 `hint`(deriveGroupPoints 统一口径:title 恒空,参考只当提示)
    const name = p?.hint ? `卖点${i + 1} · 参考:${p.hint}` : `卖点${i + 1}`;
    return makeBlock("scene", name);
  });
  const tail = [
    makeBlock("spec", "规格参数"),
    makeBlock("sales", "好评背书"),
    makeBlock("cta", "品牌 CTA"),
  ];
  return [...head, ...pointBlocks, ...tail];
}

/**
 * S2 官媒→长图:标题+分段文案铺进社交种草骨架的文字位。体裁边界 = 记者文优先/长图图优先:
 * 种子只铺文字(金句=标题、body=逐段正文),画面块(kv/scene)留默认 prompt 由用户在搭建器补。
 */
export function buildBlocksFromReporterSeed(
  seed: import("@/entities/scene-card/model/presets").LongImageReporterSeed
): LongImageBlockForm[] {
  const kv = makeBlock("kv", "主视觉");
  kv.props = { ...kv.props, prompt: `围绕「${seed.title}」的社交种草主视觉画面` };
  const golden = makeBlock("golden", "金句开场");
  golden.props = { ...golden.props, text: seed.title };
  const bodies = seed.paragraphs.map((p, i) => {
    const b = makeBlock("body", `正文段落${i + 1}`);
    b.props = { ...b.props, text: p };
    return b;
  });
  return [kv, golden, ...bodies, makeBlock("cta", "结尾 CTA")];
}

export const CANVAS_WIDTHS = [750, 1080, 1242] as const;

export interface LongImageDraft {
  direction: "v" | "h";
  useCase: UseCase;
  blocks: LongImageBlockForm[];
  wideLayout: "lr" | "tb" | "center" | "three";
  widePresetKey: string;
  width: number;
  scale: 1 | 2 | 3;
  background: "white" | "gray" | "brand";
  style: { key: string; label: string; tone?: string } | null;
  provider: string;
  blueprintId?: number;
  formulaName?: string;
  /** D2 统一视觉 DNA(阶段一产出/预设/手调);null=存量行为(background 三档推色)。 */
  styleTokens?: StyleTokens | null;
}

/** 稳定序列化(键排序)—— 只用来判「和默认值一模一样吗」,不参与提交。 */
function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(",")}]`;
  if (v && typeof v === "object") {
    return `{${Object.entries(v as Record<string, unknown>)
      .sort(([a], [c]) => (a < c ? -1 : 1))
      .map(([k, val]) => `${k}:${canonical(val)}`)
      .join(",")}}`;
  }
  return JSON.stringify(v) ?? "null";
}

/**
 * `cta` 的默认值「立即下单 →」是**能用的默认值**,不是占位符 —— 一个字没改也能出图。
 * 其余样式的默认值(「在此填写文案…」「名称/内容」「第一条/第二条」「列一/列二」
 * 「指标名」「维度」「本品 3 / 传统 30」「https://」)全是写给作者看的提示,
 * 画到成图上就是废品。区别在**这句话对读图的人有没有意义**。
 */
const USABLE_DEFAULT_TAGS = new Set(["cta"]);

/** 这块还是原封不动的占位内容吗(props 与 `defaultPropsFor` 逐字相同)。 */
export function isUntouchedPlaceholder(block: LongImageBlockForm): boolean {
  if (USABLE_DEFAULT_TAGS.has(block.styleTag)) return false;
  return canonical(block.props) === canonical(defaultPropsFor(block.styleTag));
}

/**
 * v2 模板「示例未替换」检测(2026-08-17,老板拍板「套用即所见即所得」)。
 *
 * ★为什么不能沿用 `isUntouchedPlaceholder`:v1 模板存的是**占位**(=defaultPropsFor),
 *   所以「没改过」= 「等于占位」。v2 模板存的是 admin 的**真实示例内容**(≠占位),占位锚从此失效。
 *   改用**示例基线锚**:比对 draft 块 props 与套用时的模板原始 props(按 id 对齐)逐字相同 = 用户一个字没改。
 * ★老板指定:示例未替换**不硬拦**(buildLongImagePayload 的硬闸只拦"新建时的裸占位"),
 *   这里只供 UI 层出**友好确认**(「你还没替换示例文案,将按示例内容出图」),用户确认即照出。
 * cta 这类「能用的默认值」同样跳过(它本就允许一字不改出图)。
 */
export function unreplacedExampleBlocks(
  draftBlocks: LongImageBlockForm[],
  exampleBlocks: LongImageBlockForm[]
): LongImageBlockForm[] {
  const byId = new Map(exampleBlocks.map((b) => [b.id, b]));
  return draftBlocks.filter((b) => {
    if (USABLE_DEFAULT_TAGS.has(b.styleTag)) return false;
    const ex = byId.get(b.id);
    return ex != null && canonical(b.props) === canonical(ex.props);
  });
}

/**
 * 表格块矩形化(2026-08-12,测试机 preview-bands/orchestrate 连环 422)。
 *
 * ai 侧 `TableProps` 硬校验「表格每行列数必须与表头一致」。错配的真身在编辑器:
 * 先填好数据行、再回头改表头列数 —— 表头输入框此前只更新 headers 不动 rows,
 * 之后每次实时预览、每次出图都被 422 打回,且坏状态随草稿保存(所以只改输入口
 * 救不回存量稿,送 ai 前还要一道 {@link fitTableProps})。
 *
 * 三道防线,ai 侧校验保持不动当最后防线:
 *   ① 编辑态 {@link refitTableRows}:表头列数变了顺手对齐已有行;
 *   ② 出图闸(在 {@link buildLongImagePayload} 里):行里有表头装不下的**内容**时
 *      给可跳转的人话错误 —— 静默裁掉是丢用户数据,比报错更糟;
 *   ③ 送 ai 前 {@link fitTableProps}:硬矩形化 + 钳到 ai 边界。
 */

/**
 * 编辑态:表头列数变化后对齐已有行 —— 补空到表头长;只裁**空的**尾格。
 * 有内容的多余格不裁:打字瞬时态(逗号被误删又补回)不该吞用户数据,
 * 真到出图时还错配,由出图闸报人话错误。
 */
export function refitTableRows(rows: unknown, headerCount: number): string[][] {
  return (Array.isArray(rows) ? rows : [])
    .filter((r): r is unknown[] => Array.isArray(r))
    .map((r) => {
      const fixed = r.map((c) => String(c ?? ""));
      while (fixed.length > headerCount && !fixed[fixed.length - 1].trim()) fixed.pop();
      while (fixed.length < headerCount) fixed.push("");
      return fixed;
    });
}

/**
 * 送 ai 前硬矩形化:补空 / 裁到表头长,并钳到 ai `TableProps` 边界(6 列 / 20 行 /
 * 单元格 60 字)。只对表格形状(headers+rows 双数组)的 props 生效,其余原样返回。
 * 到这一步还比表头宽的行只可能是空尾格(有内容的已被出图闸拦下),裁掉不丢数据;
 * 预览路径没有闸,裁掉多余列恰好让用户在预览里**看见**列没了。
 */
export function fitTableProps(props: Record<string, unknown>): Record<string, unknown> {
  const { headers, rows } = props as { headers?: unknown; rows?: unknown };
  if (!Array.isArray(headers) || !Array.isArray(rows) || headers.length === 0) return props;
  const hs = headers.slice(0, 6).map((h) => String(h ?? ""));
  const fitted = rows
    .filter((r): r is unknown[] => Array.isArray(r))
    .slice(0, 20)
    .map((r) => {
      const fixed = r.slice(0, hs.length).map((c) => String(c ?? "").slice(0, 60));
      while (fixed.length < hs.length) fixed.push("");
      return fixed;
    });
  return { ...props, headers: hs, rows: fitted };
}

/**
 * 表格块「送去 ai 必 422」的形状缺失(表头为空 / 一行数据都没有)。
 * 实时预览是**整批**请求过 ai 请求级校验,一个这样的块会打掉全部块的预览,
 * 所以预览要把它排除在批量请求外(该块回落版式示意)。
 */
export function tableShapeMissing(props: Record<string, unknown>): boolean {
  const { headers, rows } = props as { headers?: unknown; rows?: unknown };
  if (!Array.isArray(headers) || !Array.isArray(rows)) return false;
  return headers.length === 0 || !rows.some((r) => Array.isArray(r));
}

/**
 * 实时预览的**通用**形状闸:这块现在送去必被 ai 请求级校验拒 → 不进批量请求。
 *
 * ★2026-08-12 测试机(fix-0812)实据驱动,不是猜的:8 小时窗口 preview-bands 313 次请求
 *   里 81 次被打回(26%),构成 = 列表条目空 24 / 表格行列错配 15 / 条目 label 空 ~19 /
 *   正文 text 空 4 / 图表 series 数值非数 4、系列空 2、系列 label 空 1。
 *   全是同一件事:用户**编辑中的瞬时空态**被送去硬校验,而 preview-bands 整批过校验 ——
 *   一块坏,右列**整列**掉回版式示意(用户看到的就是「预览时好时坏」)。
 * ★与出图闸的口径差:出图闸要报错点名(那时该修);预览闸只安静跳过 —— 打字过程中弹错是打扰。
 * ★判据只认「ai 那边确实会拒」的几种形状;不认识的形状一律放行(宁可让 ai 说话,
 *   也不静默吞掉本来能渲的块)。
 */
export function previewShapeMissing(props: Record<string, unknown>): boolean {
  if (!props || typeof props !== "object") return false;
  if (tableShapeMissing(props)) return true;

  const p = props as {
    items?: unknown;
    text?: unknown;
    series?: unknown;
  };

  // 文本族:text 是必填且 min_length=1
  if (typeof p.text === "string" && p.text.trim() === "") return true;

  // 列表族(numlist/twocol/pairs/stat…):items 至少一条,且每条 label 非空
  if (Array.isArray(p.items)) {
    if (p.items.length === 0) return true;
    if (
      p.items.some(
        (it) =>
          it &&
          typeof it === "object" &&
          typeof (it as { label?: unknown }).label === "string" &&
          (it as { label: string }).label.trim() === ""
      )
    ) {
      return true;
    }
  }

  // 图表族:series 至少一条,label 非空,value 必须是数(输入框给的字符串数字算数)
  if (Array.isArray(p.series)) {
    if (p.series.length === 0) return true;
    if (
      p.series.some((s) => {
        if (!s || typeof s !== "object") return false;
        const { label, value } = s as { label?: unknown; value?: unknown };
        if (typeof label === "string" && label.trim() === "") return true;
        if (value === "" || value === null || value === undefined) return true;
        return typeof value === "string" && !Number.isFinite(Number(value));
      })
    ) {
      return true;
    }
  }

  return false;
}

/**
 * 出图前校验没过 —— **带得出是哪一块**(2026-07-30,7.29 反馈任务 13)。
 *
 * ★缺陷:以前一律 `throw new Error(字符串)`,上层只能弹一句 toast。8 个块时用户拿着
 *   「图块『核心卖点』还没有画面描述」自己逐个对名字 —— 玉玉那条的注脚是「爱民已经告知
 *   方法」,**靠人传口令的功能等于没有**。
 * ★继承 Error 而不是另起一个类型:上层有一条 `e instanceof Error && !(e instanceof ApiError)`
 *   的分流(429 配额别被说成「还差一点」),换成裸对象会从那条分支掉出去。
 */
export class LongImageValidationError extends Error {
  /** 出问题的块(可能多块);空数组 = 整体性问题(如块数与版式不符),没有可跳转的目标。 */
  readonly issues: Array<{ blockId: string; blockName: string; message: string }>;

  constructor(
    message: string,
    issues: Array<{ blockId: string; blockName: string; message: string }> = []
  ) {
    super(message);
    this.name = "LongImageValidationError";
    this.issues = issues;
  }
}

/**
 * 多条问题 → 一句话摘要(toast 标题下那行)。
 *
 * 只列前两块的名字 + 「等 N 个区块」:一次甩八条块名读不动,而用户真正要的是「点一下跳过去」,
 * 详细逐条由错误面板给。
 */
function summarizeIssues(
  issues: Array<{ blockId: string; blockName: string; message: string }>
): string {
  const blocks = [...new Set(issues.map((i) => i.blockName))];
  if (blocks.length === 1) {
    return `区块「${blocks[0]}」${issues[0].message}`;
  }
  const head = blocks
    .slice(0, 2)
    .map((n) => `「${n}」`)
    .join("");
  return `${blocks.length} 个区块还没填好:${head}${blocks.length > 2 ? " 等" : ""} —— 点下面任一条跳过去`;
}

/** 组装提交 payload(与 ai LongImageRequest 对齐;校验失败抛 {@link LongImageValidationError})。 */
/** LI30-B:原图直出块判据(canvas.originalKeys 槽在场)。结构性访问,不 import
 *  canvasBridge 的类型 —— 那边 import 本文件,引回来就是环。 */
function originalSlotOf(
  b: LongImageBlockForm
): { originalKeys?: string[]; originalFit?: "grid" | "full" } | null {
  const c = (b as { canvas?: { originalKeys?: unknown; originalFit?: unknown } }).canvas;
  if (!c || !Array.isArray(c.originalKeys)) return null;
  return {
    originalKeys: c.originalKeys.filter((k): k is string => typeof k === "string" && !!k),
    ...(c.originalFit === "full" ? { originalFit: "full" as const } : {}),
  };
}

/**
 * 排屏标注(一键成品「回搭建器改这一稿」带回的屏号,见 editSeed `parseScreenAnno`)。
 * 手工搭建的块没有这一格 → undefined,提交体逐字不变;有就原样回给 ai,
 * 让重提按原来的屏出图(不带 = ai 重新装箱,屏边界漂移)。结构性访问,理由同上。
 */
function screenAnnoOf(b: LongImageBlockForm): unknown {
  return (b as { canvas?: { screenAnno?: unknown } }).canvas?.screenAnno ?? undefined;
}

/** 标注块的渲染档(种子照实带回的 A/B/C;缺 = 未指定,不发键)。只跟屏号一起发,手工块逐字不变。 */
function renderModeOf(b: LongImageBlockForm): "A" | "B" | "C" | undefined {
  const m = (b as { canvas?: { renderMode?: unknown } }).canvas?.renderMode;
  return m === "A" || m === "B" || m === "C" ? m : undefined;
}

export function buildLongImagePayload(draft: LongImageDraft): LongImageCreatePayload {
  if (draft.blocks.length === 0) {
    throw new LongImageValidationError("至少需要 1 个区块");
  }
  const preset = WIDE_PRESETS.find((p) => p.key === draft.widePresetKey) ?? WIDE_PRESETS[0];
  const isWide = draft.direction === "h";
  /**
   * ★结构性错误先报(2026-07-30 保留既有次序):「横宽版式需要 2 个分区块」比「某块没填」
   * 更根本 —— 两条一起犯时先报块数,否则用户填完内容才发现块数还是不对。
   * 它没有可跳转的块,issues 留空。
   */
  if (isWide) {
    const zones = WIDE_LAYOUTS.find((l) => l.key === draft.wideLayout)?.zones ?? 2;
    if (draft.blocks.length !== zones) {
      throw new LongImageValidationError(`横宽版式「${draft.wideLayout}」需要 ${zones} 个分区块`);
    }
  }
  /**
   * ★逐块问题**一次收全**(7.29 反馈任务 13):以前是遇到第一条就 throw,用户填完这块提交、
   * 再被告知下一块 —— 8 个块能来回七趟。现在收齐再抛,并且每条带 blockId,前端据此
   * 「点错误 → 滚到该块 + 高亮」,左侧列表也能给未完成块挂红点。
   */
  const issues: Array<{ blockId: string; blockName: string; message: string }> = [];
  for (const b of draft.blocks) {
    // ★原图直出块(LI30-B):画面就是用户的成图,不需要画面描述 —— 它的必填是「选了图」。
    const orig = originalSlotOf(b);
    if (orig) {
      if (!orig.originalKeys?.length) {
        issues.push({ blockId: b.id, blockName: b.name, message: "还没选要拼入的成图" });
      }
      continue;
    }
    if (b.primitive === "image") {
      const prompt = String((b.props as { prompt?: unknown }).prompt ?? "").trim();
      if (!prompt || prompt === IMAGE_PROMPT_PLACEHOLDER) {
        issues.push({ blockId: b.id, blockName: b.name, message: "还没有画面描述" });
      }
      const genMode = (b.props as { genMode?: unknown }).genMode;
      const pr = b.props as { refUrl?: unknown; refKey?: unknown };
      if (genMode === "i2i" && !pr.refKey && !pr.refUrl) {
        issues.push({
          blockId: b.id,
          blockName: b.name,
          message: "选择了参考图生图,但还没上传参考图",
        });
      }
    }
    const series = (b.props as { series?: unknown }).series;
    if (Array.isArray(series)) {
      // 任务 8:图表块数值解析不出时不落 0 而留 null,这里把 null 拦在浏览器里,
      // 不让它去 ai 换回一句语焉不详的 422。
      const blanks = series.filter(
        (s) =>
          (s as { value?: unknown })?.value === null ||
          (s as { value?: unknown })?.value === undefined
      ).length;
      if (blanks > 0) {
        issues.push({
          blockId: b.id,
          blockName: b.name,
          message: `有 ${blanks} 行还没填数值 —— 这一块画横条图、只吃数字;要做文字对照请换成「前后对比」块`,
        });
      }
    }
    const tp = b.props as { headers?: unknown; rows?: unknown };
    if (Array.isArray(tp.headers) && Array.isArray(tp.rows)) {
      // 表格块错配在这里说人话,别让它去 ai 换一句「表格每行列数必须与表头一致」的 422
      // (2026-08-12 测试机连环报障)。窄行不打扰用户 —— fitTableProps 补空放行。
      const headerCount = tp.headers.length;
      const rows = tp.rows.filter((r): r is unknown[] => Array.isArray(r));
      if (headerCount === 0) {
        issues.push({
          blockId: b.id,
          blockName: b.name,
          message: "表头还没填 —— 在「表头」里用逗号分隔列名,最多 6 列",
        });
      } else if (rows.length === 0) {
        issues.push({
          blockId: b.id,
          blockName: b.name,
          message: "还没有数据行 —— 一行一条,列用 | 分隔",
        });
      } else {
        const wide = rows.findIndex((r) =>
          r.slice(headerCount).some((c) => String(c ?? "").trim())
        );
        if (wide >= 0) {
          issues.push({
            blockId: b.id,
            blockName: b.name,
            message:
              `第 ${wide + 1} 行有 ${rows[wide].length} 列内容,表头只有 ${headerCount} 列 —— ` +
              `给表头补上列名,或删掉行里多出的内容`,
          });
        }
      }
    }
  }
  if (issues.length) {
    throw new LongImageValidationError(summarizeIssues(issues), issues);
  }
  /**
   * ★2026-07-30 模板走查抓到:套模板 → 只填了两个图块的画面描述 → 出图,
   * 成品上「在此填写文案…」被画成 100px 的大字标题,「名称/内容」「第一条/第二条」
   * 原样进表格。**花了 2,100 积分买一张写着占位符的图。**
   *
   * 图块那条闸(「在此描述画面…」)从一开始就在,文案/数据块却一直没有 —— 因为在
   * D8 之前模板表是空的,没人走得到这条路。模板一造出来,这个坑就对所有人打开了。
   *
   * 判据是「props 与默认值逐字相同」而不是关键词黑名单:改过一个字就说明用户看过这块,
   * 剩下的由他负责;而没改过的,我们比他更清楚那是占位符。
   *
   * ★放在结构校验**之后**:「横宽版式需要 2 个分区块」是更根本的错,先说那个;
   *   两条一起犯时先报占位会让用户去填内容,填完才发现块数还是不对。
   */
  const untouched = draft.blocks.filter(
    (b) => originalSlotOf(b) == null && isUntouchedPlaceholder(b)
  );
  if (untouched.length) {
    const names = untouched.map((b) => `「${b.name}」`).join("");
    throw new LongImageValidationError(
      `${untouched.length} 个区块还是模板占位内容:${names} —— ` +
        `填上你自己的文案再出图,否则占位符会被画进成品`,
      untouched.map((b) => ({
        blockId: b.id,
        blockName: b.name,
        message: "还是模板占位内容,没改过",
      }))
    );
  }
  return {
    canvas: {
      direction: draft.direction,
      width: draft.width,
      scale: draft.scale,
      background: draft.background,
      useCase: draft.useCase,
      ...(isWide
        ? { wideLayout: draft.wideLayout, wideWidth: preset.w, wideHeight: preset.h }
        : {}),
    },
    style: draft.style ?? { key: "free", label: "自由发挥" },
    provider: draft.provider as LongImageCreatePayload["provider"],
    blocks: draft.blocks.map((b) => {
      const orig = originalSlotOf(b);
      const screenAnno = screenAnnoOf(b);
      return {
        id: b.id,
        primitive: b.primitive,
        styleTag: b.styleTag,
        name: b.name,
        // 表格块矩形化兜底:存量草稿里已经躺着行列错配的数据,光改编辑器救不回它们;
        // 图键剥除同理(0917:存量脏稿里可能已躺着带 refKey/genMode 的文本块)
        props: fitTableProps(stripImageOnlyProps(b.primitive, b.props)),
        // ★原件槽(LI30-B):手工路此前不带这两格 = 直出块提交即退化成普通 AI 图块
        ...(orig?.originalKeys?.length
          ? {
              originalKeys: orig.originalKeys,
              ...(orig.originalFit ? { originalFit: orig.originalFit } : {}),
            }
          : {}),
        ...(screenAnno ? { screenAnno } : {}),
        ...(screenAnno && renderModeOf(b) ? { renderMode: renderModeOf(b) } : {}),
      };
    }),
    blueprintId: draft.blueprintId,
    formulaName: draft.formulaName,
    ...(draft.styleTokens ? { styleTokens: draft.styleTokens } : {}),
  };
}
